import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import MessageModal from "../../components/shared/MessageModal";
import "./SettingsEditor.css";
import { various, settingsEditor } from "../../Data/siteContent";

const SYSEX_HEADER = [0xf0, 0x00, 0x02, 0x0d];
const SYSEX_END = 0xf7;
const SYSEX_CMD_READ_RESPONSE = 0x02;

const SYNC_FORMAT_OPTIONS = [
  { value: 0, label: "24 PPQ" },
  { value: 1, label: "1 PPQ" },
  { value: 2, label: "2 PPQ" },
  { value: 3, label: "4 PPQ" },
  { value: 4, label: "8 PPQ" },
  { value: 5, label: "12 PPQ" },
  { value: 6, label: "48 PPQ" },
];

const RECORDER_QUANTIZE_OPTIONS = [
  { value: 0, label: "OFF" },
  { value: 1, label: "1/32" },
  { value: 2, label: "1/16" },
  { value: 3, label: "1/8" },
  { value: 4, label: "1/4" },
  { value: 5, label: "1/2" },
  { value: 6, label: "1/1" },
];

const RECORDER_START_MODE_OPTIONS = [
  { value: 0, label: "First Event" },
  { value: 1, label: "Immediate" },
];

const firmwareAtLeast = (version, major, minor) => {
  if (!version) return false;
  const match = version.match(/v(\d+)\.(\d+)/);
  if (!match) return false;
  const maj = Number(match[1]);
  const min = Number(match[2]);
  return maj > major || (maj === major && min >= minor);
};

const DEFAULT_SELECTIONS = {
  usb: { keyboard: "1", bass: "2", touchbar: "3" },
  midi1: { keyboard: "1", bass: "2", touchbar: "3" },
  midi2: { keyboard: "1", bass: "2", touchbar: "3" },
  syncFormat: "0",
  octaveShiftMode: "0",
  recorderQuantize: "0",
  recorderStartMode: "0",
};

function SettingsEditor({ feature, productId }) {
  const { usbDeviceName: deviceName, labels } = feature;
  const [searchParams] = useSearchParams();
  const devMode = searchParams.get("dev") === "true";

  const [midiOutputs, setMidiOutputs] = useState([]);
  const [output, setOutput] = useState(null);
  const [input, setInput] = useState(null);
  const [midiError, setMidiError] = useState("");
  const [showMidiError, setShowMidiError] = useState(false);
  const [firmwareVersion, setFirmwareVersion] = useState(null);
  const [midiSelections, setMidiSelections] = useState(DEFAULT_SELECTIONS);

  const readTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);

  const applySelections = useCallback(data => {
    setMidiSelections({
      usb: {
        keyboard: String(data[5] + 1),
        bass: String(data[6] + 1),
        touchbar: String(data[7] + 1),
      },
      midi1: {
        keyboard: String(data[8] + 1),
        bass: String(data[9] + 1),
        touchbar: String(data[10] + 1),
      },
      midi2: {
        keyboard: String(data[11] + 1),
        bass: String(data[12] + 1),
        touchbar: String(data[13] + 1),
      },
      syncFormat: String(data[14]),
      octaveShiftMode: String(data[15]),
      recorderQuantize: String(data[16]),
      recorderStartMode: String(data[17]),
    });
  }, []);

  const handleReadResponse = useCallback(
    data => {
      if (readTimeoutRef.current) {
        clearTimeout(readTimeoutRef.current);
        readTimeoutRef.current = null;
      }
      applySelections(data);
      setFirmwareVersion(`v${data[18]}.${data[19]}`);
    },
    [applySelections]
  );

  const connectMidi = useCallback(() => {
    if (devMode) return;

    navigator
      .requestMIDIAccess({ sysex: true })
      .then(access => {
        const nextOutputs = [];
        let matchedOutput = null;
        let matchedInput = null;

        for (const out of access.outputs.values()) {
          nextOutputs.push(out);
          if (out.name.toUpperCase().includes(deviceName.toUpperCase())) {
            matchedOutput = out;
          }
        }
        for (const inp of access.inputs.values()) {
          if (inp.name.toUpperCase().includes(deviceName.toUpperCase())) {
            matchedInput = inp;
          }
        }

        setMidiOutputs(nextOutputs);
        if (matchedOutput) setOutput(matchedOutput);
        if (matchedInput) setInput(matchedInput);
      })
      .catch(error => {
        if (error && error.name === "NotAllowedError") {
          setMidiError(various.midiGrantError1);
        } else {
          setMidiError(error?.message || various.midiGrantError2);
        }
        setShowMidiError(true);
      });
  }, [deviceName, devMode]);

  useEffect(() => {
    connectMidi();
  }, [connectMidi]);

  useEffect(() => {
    if (!input) return;

    const onMidiMessage = event => {
      const data = event.data;
      const matches =
        data.length === 21 &&
        data[0] === 0xf0 &&
        data[1] === 0x00 &&
        data[2] === 0x02 &&
        data[3] === 0x0d &&
        data[4] === SYSEX_CMD_READ_RESPONSE;

      if (matches) {
        handleReadResponse(data);
      }
    };

    input.addEventListener("midimessage", onMidiMessage);
    return () => input.removeEventListener("midimessage", onMidiMessage);
  }, [input, handleReadResponse]);

  useEffect(() => {
    return () => {
      if (readTimeoutRef.current) clearTimeout(readTimeoutRef.current);
    };
  }, []);

  const handleSelectChange = (section, key, value) => {
    if (typeof DEFAULT_SELECTIONS[section] === "object") {
      setMidiSelections(prev => ({
        ...prev,
        [section]: { ...prev[section], [key]: value },
      }));
    } else {
      setMidiSelections(prev => ({ ...prev, [section]: value }));
    }
  };

  function buildSysexWriteMessage() {
    const ch = s => parseInt(s, 10) - 1;
    const v = s => parseInt(s, 10);

    const dataBytes = [
      ch(midiSelections.usb.keyboard),
      ch(midiSelections.usb.bass),
      ch(midiSelections.usb.touchbar),
      ch(midiSelections.midi1.keyboard),
      ch(midiSelections.midi1.bass),
      ch(midiSelections.midi1.touchbar),
      ch(midiSelections.midi2.keyboard),
      ch(midiSelections.midi2.bass),
      ch(midiSelections.midi2.touchbar),
      v(midiSelections.syncFormat),
      v(midiSelections.octaveShiftMode),
      v(midiSelections.recorderQuantize),
      v(midiSelections.recorderStartMode),
    ];

    const reserved = new Array(16).fill(0x00);
    return new Uint8Array([...SYSEX_HEADER, ...dataBytes, ...reserved, SYSEX_END]);
  }

  function handleSendSysex() {
    const message = buildSysexWriteMessage();
    if (devMode) {
      console.log(
        "DEV MODE — SysEx WRITE message:",
        Array.from(message)
          .map(b => "0x" + b.toString(16).padStart(2, "0"))
          .join(" ")
      );
      return;
    }
    output.send(message);
  }


  function handleSaveToFile() {
    const fileData = { selections: midiSelections };
    const blob = new Blob([JSON.stringify(fileData, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${deviceName.toLowerCase()}_settings.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function handleLoadFromFile(event) {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = e => {
      try {
        const fileData = JSON.parse(e.target.result);
        if (fileData.selections) {
          setMidiSelections(fileData.selections);
        }
      } catch {
        setMidiError("Invalid settings file.");
        setShowMidiError(true);
      }
    };
    reader.readAsText(file);
    event.target.value = "";
  }

  const isConnected = devMode || midiOutputs.some(e => e.name.toUpperCase().includes(deviceName.toUpperCase()));
  const canSend = devMode || (output && output.name.toUpperCase().includes(deviceName.toUpperCase()));

  const renderChannelSelect = (section, instrument, label) => (
    <div className="settings-editor__form-group">
      <label>
        {label}
        <select
          className="settings-editor__select"
          value={midiSelections[section][instrument]}
          onChange={e => handleSelectChange(section, instrument, e.target.value)}
        >
          {Array.from({ length: 16 }, (_, i) => (
            <option key={i + 1} value={i + 1}>
              {i + 1}
            </option>
          ))}
        </select>
      </label>
    </div>
  );

  const hasAdvancedFeatures = firmwareAtLeast(firmwareVersion, 1, 1);

  const renderOptionSelect = (stateKey, label, options, { isOptionDisabled, tooltip } = {}) => (
    <div className="settings-editor__form-group">
      <label>
        {label}
        <select
          className="settings-editor__select"
          value={midiSelections[stateKey]}
          onChange={e => handleSelectChange(stateKey, null, e.target.value)}
          title={tooltip || undefined}
        >
          {options.map(opt => (
            <option key={opt.value} value={opt.value} disabled={isOptionDisabled?.(opt)}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>
    </div>
  );

  return (
    <div className="settings-editor__container">
      <h1 className="settings-editor__title">
        {deviceName} {settingsEditor.title}
      </h1>

      {(() => {
        const parts = settingsEditor.disclaimer.replace("{device}", deviceName).split("{link}");
        return (
          <div className="settings-editor__disclaimer">
            {parts[0]}
            <Link to={`/products/${productId}/updates`}>{settingsEditor.disclaimerLinkText}</Link>
            {parts[1]}
          </div>
        );
      })()}

      {devMode && <div className="settings-editor__dev-badge">{settingsEditor.devBadge}</div>}

      <div className="settings-editor__section">
        {!isConnected ? (
          <div>
            {deviceName} {settingsEditor.notConnected}
            <br />
            {(() => {
              const parts = settingsEditor.notConnectedHelp.replace("{device}", deviceName).split("{link}");
              return (
                <>
                  {parts[0]}
                  <button type="button" className="settings-editor__inline-link" onClick={connectMidi}>
                    {settingsEditor.notConnectedHelpLinkText}
                  </button>
                  {parts[1]}
                </>
              );
            })()}
          </div>
        ) : (
          <div>
            {deviceName} {settingsEditor.connected}
            <br />
            {settingsEditor.connectedHelp.replaceAll("{device}", deviceName)}
            <br />
            {settingsEditor.connectedHelp2.replaceAll("{device}", deviceName)}
            <br />
            <br />
            <div className="settings-editor__button-group">
              <button
                className="settings-editor__button"
                disabled
                title={settingsEditor.featureLockedTooltip}
              >
                {settingsEditor.buttonLoadDevice.replace("{device}", deviceName)}
              </button>
              <button className="settings-editor__button" onClick={handleSendSysex} disabled={!canSend}>
                {settingsEditor.buttonSaveDevice.replace("{device}", deviceName)}
              </button>
              <button className="settings-editor__button" onClick={() => fileInputRef.current.click()}>
                {settingsEditor.buttonLoadFile}
              </button>
              <button className="settings-editor__button" onClick={handleSaveToFile}>
                {settingsEditor.buttonSaveFile}
              </button>
            </div>
            <input ref={fileInputRef} type="file" accept=".json" onChange={handleLoadFromFile} hidden />
            <div className="settings-editor__firmware">
              {firmwareVersion ? `${settingsEditor.firmwareLabel} ${firmwareVersion}` : "\u00A0"}
            </div>
          </div>
        )}
      </div>

      <table>
        <tbody>
          <tr className="tr-table-settings">
            <td className="td-settings-table">
              <div className="title-td-table-settings">{labels.sectionUsbMidi}</div>
              <div className="title-td-table-settings-second">{labels.outputChannels}</div>
              <div className="settings-container-forms">
                {renderChannelSelect("usb", "keyboard", labels.labelKeyboard)}
                {renderChannelSelect("usb", "bass", labels.labelBass)}
                {renderChannelSelect("usb", "touchbar", labels.labelTouchbar)}
              </div>
            </td>

            <td className="td-settings-table">
              <div className="title-td-table-settings">{labels.sectionMidi1}</div>
              <div className="title-td-table-settings-second">{labels.outputChannels}</div>
              <div className="settings-container-forms">
                {renderChannelSelect("midi1", "keyboard", labels.labelKeyboard)}
                {renderChannelSelect("midi1", "bass", labels.labelBass)}
                {renderChannelSelect("midi1", "touchbar", labels.labelTouchbar)}
              </div>
            </td>

            <td className="td-settings-table">
              <div className="title-td-table-settings">{labels.sectionMidi2}</div>
              <div className="title-td-table-settings-second">{labels.outputChannels}</div>
              <div className="settings-container-forms">
                {renderChannelSelect("midi2", "keyboard", labels.labelKeyboard)}
                {renderChannelSelect("midi2", "bass", labels.labelBass)}
                {renderChannelSelect("midi2", "touchbar", labels.labelTouchbar)}
              </div>
            </td>

            <td className="td-settings-table">
              <div className="title-td-table-settings">{labels.sectionSync}</div>
              <div className="settings-container-forms">
                {renderOptionSelect("syncFormat", labels.labelFormat, SYNC_FORMAT_OPTIONS, {
                  isOptionDisabled: opt => !hasAdvancedFeatures && opt.value !== 0,
                  tooltip: !hasAdvancedFeatures ? settingsEditor.featureLockedTooltip : undefined,
                })}
              </div>
            </td>

            <td className="td-settings-table">
              <div className="title-td-table-settings">{labels.sectionKeyboard}</div>
              <div className="settings-container-forms">
                {renderOptionSelect("octaveShiftMode", labels.labelOctaveShiftMode, [
                  { value: 0, label: "Retrigger" },
                  { value: 1, label: "Classic" },
                ])}
              </div>
            </td>

            <td
              className={`td-settings-table${!hasAdvancedFeatures ? " td-settings-table--disabled" : ""}`}
              title={!hasAdvancedFeatures ? settingsEditor.featureLockedTooltip : undefined}
            >
              <div className="title-td-table-settings">{labels.sectionRecorder}</div>
              <div className="settings-container-forms">
                {renderOptionSelect("recorderQuantize", labels.labelQuantize, RECORDER_QUANTIZE_OPTIONS)}
                {renderOptionSelect("recorderStartMode", labels.labelStartMode, RECORDER_START_MODE_OPTIONS)}
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <MessageModal
        isVisible={showMidiError}
        title="Error"
        message={midiError}
        type="error"
        closeLabel="Close"
        onClose={() => setShowMidiError(false)}
      />
    </div>
  );
}

export default SettingsEditor;
